import {
  EARNINGS_RANGE_DAYS,
  type EarningsByDayPart,
  type EarningsRange,
} from "@pax/api-contract/accounting/v1alpha1";
import { panelMotion } from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { creditsFromRaw } from "@/features/credits/format";
import { Tabs } from "@/features/evals/components";
import { ChartControls } from "@/features/ledger/charts/ChartControls";
import { HighlightScope } from "@/features/ledger/charts/highlight";
import { PeriodStepper } from "@/features/ledger/charts/PeriodStepper";
import { StackedBars } from "@/features/ledger/charts/StackedBars";
import { formatChartCredits, formatPeriod, largestFirst } from "@/features/ledger/charts/shared";
import { CopyId, Credits, Note, Table } from "@/features/ledger/components";
import { everyByDefault, Tags } from "@/features/ledger/pickers";
import { Problem } from "@/features/staff/components";
import { isNotFound } from "@/lib/api";
import { type EarningsFilter, earningsByDayQuery } from "./queries";

const DAY_MS = 24 * 60 * 60 * 1000;

const rangeLabels: Record<EarningsRange, string> = {
  "30d": "30 days",
  "90d": "90 days",
  "365d": "365 days",
};

/** The UTC days an earnings range covers `back` ranges before the newest, as the API reads them. */
function earningsPeriod(range: EarningsRange, back: number) {
  const length = EARNINGS_RANGE_DAYS[range] * DAY_MS;
  const to = Date.parse(new Date().toISOString().slice(0, 10)) + DAY_MS - back * length;
  return { from: new Date(to - length), to: new Date(to) };
}

export function EarningsRangeTabs({
  range,
  onChange,
}: {
  range: EarningsRange;
  onChange: (range: EarningsRange) => void;
}) {
  return (
    <Tabs
      compact
      value={range}
      options={(Object.keys(rangeLabels) as EarningsRange[]).map(value => ({
        value,
        label: rangeLabels[value],
      }))}
      onChange={onChange}
    />
  );
}

const utcDayFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

/** The UTC days a read covers, first to last: `to` is the day after the last. */
const daysCovered = (from: string, to: string) =>
  `${utcDayFormat.format(new Date(from))} – ${utcDayFormat.format(new Date(Date.parse(to) - DAY_MS))}, UTC days`;

/** The last UTC day creator rewards are paid for: `to` is the day after it. */
function PaidThrough({ to }: { to: string }) {
  return (
    <p className="text-xs text-muted-foreground">
      Paid through {utcDayFormat.format(new Date(Date.parse(to) - DAY_MS))}, UTC day
    </p>
  );
}

/** Every UTC day from `from` up to `to`, as midnight instants. */
function daysBetween(from: string, to: string) {
  const days: string[] = [];
  for (let day = Date.parse(from); day < Date.parse(to); day += DAY_MS) {
    days.push(new Date(day).toISOString());
  }
  return days;
}

const MIN_CHART_DAYS = 7;

/**
 * The first day to chart: the first day anything was earned, so a short history fills the chart,
 * but never fewer than `MIN_CHART_DAYS` and never before the range starts.
 */
function chartFrom(from: string, to: string, firstEarnedOn: string | undefined) {
  if (!firstEarnedOn) return from;
  const latest = Math.min(Date.parse(firstEarnedOn), Date.parse(to) - MIN_CHART_DAYS * DAY_MS);
  return new Date(Math.max(Date.parse(from), latest)).toISOString().slice(0, 10);
}

/** A preset id, `other` for the presets a creator does not own, or a recipient's user id. */
const partKey = (part: EarningsByDayPart) => part.userId ?? part.presetId ?? "other";

const earnedRaw = (days: readonly { earnedRaw: string }[]) =>
  days.reduce((total, day) => total + BigInt(day.earnedRaw), 0n).toString();

const rewardsPaid = (days: readonly { rewardCount?: number }[]) =>
  days.reduce((total, day) => total + (day.rewardCount ?? 0), 0);

const rewardsLabel = (count: number) =>
  `${count.toLocaleString()} ${count === 1 ? "reward" : "rewards"}`;

/**
 * Creator rewards paid for a preset, to a creator, or to a creator from one preset: daily bars
 * stacked by the creator's presets or the preset's recipients, and what each part earned.
 */
export function PresetEarnings({
  filter,
  range,
  back = 0,
  onBackChange,
  parts: pickedParts,
  onPartsChange,
  recipientIds = false,
  counted = false,
  notFound,
}: {
  filter: EarningsFilter;
  range: EarningsRange;
  /** How many ranges back from the newest. */
  back?: number;
  onBackChange: (back: number) => void;
  parts: readonly string[];
  onPartsChange: (parts: string[] | undefined) => void;
  /** A copy button for each recipient's user id beside their name, for staff. */
  recipientIds?: boolean;
  /** How many rewards each part and the total were paid in, for staff. */
  counted?: boolean;
  /** Shown in place of the error when the caller may not read these earnings. */
  notFound?: ReactNode;
}) {
  const byDay = useQuery(earningsByDayQuery(filter, range, back));
  if (byDay.error) {
    return notFound && isNotFound(byDay.error) ? notFound : <Problem error={byDay.error} />;
  }
  if (!byDay.data) return <Note>Loading…</Note>;
  const { from, to } = byDay.data;
  const allParts =
    byDay.data.parts.length > 0
      ? byDay.data.parts
      : [{ label: "Earned", days: byDay.data.days } satisfies EarningsByDayPart];
  const pick = everyByDefault(pickedParts, byDay.data.parts.map(partKey));
  const parts = largestFirst(
    byDay.data.parts.length === 0
      ? allParts
      : allParts.filter(part => pick.values.includes(partKey(part))),
    part => BigInt(earnedRaw(part.days)),
  );
  const earnedByPartDay = parts.map(
    part => new Map(part.days.map(day => [day.earnedOn, day.earnedRaw])),
  );
  const period = earningsPeriod(range, back);
  return (
    <HighlightScope>
      <div
        aria-busy={byDay.isPlaceholderData}
        className={`space-y-4 ${panelMotion} ${byDay.isPlaceholderData ? "opacity-60" : ""}`}
      >
        {byDay.data.parts.length > 0 && (
          <Tags
            label={filter.presetId ? "Recipients" : "Presets"}
            options={byDay.data.parts.map(part => ({ value: partKey(part), label: part.label }))}
            values={pick.values}
            onChange={keys => onPartsChange(pick.keep(keys))}
          />
        )}
        <ChartControls
          controls={
            <PeriodStepper
              back={back}
              label={formatPeriod(period.from, period.to, "day")}
              onChange={onBackChange}
            />
          }
        >
          {back === 0 && byDay.data.paidThrough && <PaidThrough to={byDay.data.paidThrough} />}
          {byDay.data.days.length === 0 ? (
            <Note>Nothing was earned in this range.</Note>
          ) : parts.length === 0 ? (
            <Note>None of the picked parts earned in this range.</Note>
          ) : (
            <>
              <StackedBars
                title="Preset earnings per day"
                series={parts.map(part => ({ key: partKey(part), label: part.label }))}
                buckets={daysBetween(
                  back === 0 ? chartFrom(from, to, byDay.data.days[0]?.earnedOn) : from,
                  to,
                ).map(start => ({
                  start,
                  values: earnedByPartDay.map(earned =>
                    creditsFromRaw(earned.get(start.slice(0, 10)) ?? "0"),
                  ),
                }))}
                step="day"
                summary={
                  <>
                    <Credits raw={earnedRaw(parts.flatMap(part => part.days))} /> earned
                    {counted &&
                      ` across ${rewardsLabel(rewardsPaid(parts.flatMap(part => part.days)))}`}{" "}
                    {back === 0 ? `in the last ${rangeLabels[range]}` : "in this period"}{" "}
                    <span className="text-muted-foreground">({daysCovered(from, to)})</span>
                  </>
                }
                format={formatChartCredits}
                rest={{
                  key: "other",
                  label: filter.presetId ? "Other recipients" : "Other presets",
                }}
              />
              {byDay.data.parts.length > 0 && (
                <Table
                  columns={[
                    { label: filter.presetId ? "Recipient" : "Preset" },
                    ...(counted ? [{ label: "Rewards paid", numeric: true }] : []),
                    { label: "Earned", numeric: true },
                  ]}
                  rows={parts.map(part => ({
                    key: partKey(part),
                    cells: [
                      part.userId && recipientIds ? (
                        <span
                          key="part"
                          className="inline-flex items-baseline gap-1.5"
                          title={part.userId}
                        >
                          {part.label}
                          <CopyId id={part.userId} label="user id" />
                        </span>
                      ) : (
                        part.label
                      ),
                      ...(counted ? [rewardsPaid(part.days).toLocaleString()] : []),
                      <Credits key="earned" raw={earnedRaw(part.days)} />,
                    ],
                  }))}
                />
              )}
            </>
          )}
        </ChartControls>
      </div>
    </HighlightScope>
  );
}
